import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRecordPayment } from '@/hooks/invoicing';
import type { CustomerInvoicePaymentRow } from '@/hooks/invoicing/useCustomerInvoices';

export const RefundDialog = ({
  open, onOpenChange, invoiceId, amountPaid, payments,
}: {
  open: boolean; onOpenChange: (o: boolean) => void;
  invoiceId: string; amountPaid: number; payments: CustomerInvoicePaymentRow[];
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [originalPaymentId, setOriginalPaymentId] = useState<string>('');
  const recordPayment = useRecordPayment();

  const refundAmt = Number(amount) || 0;
  const overRefund = refundAmt > amountPaid + 0.01;

  const submit = async () => {
    if (refundAmt <= 0 || !reason.trim()) return;
    await recordPayment.mutateAsync({
      invoiceId, kind: 'refund',
      enteredAmount: 0, appliedAmount: -refundAmt, changeGiven: 0,
      method: 'other',
      refundOfPaymentId: originalPaymentId || undefined,
      notes: reason.trim(),
    });
    onOpenChange(false);
    setAmount(''); setReason(''); setOriginalPaymentId('');
  };

  const eligiblePayments = payments.filter((p) => p.kind === 'payment' && !p.voided_at);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Issue refund</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">Refundable balance: ${amountPaid.toFixed(2)}</div>
          <div className="space-y-2">
            <Label>Original payment</Label>
            <Select value={originalPaymentId} onValueChange={setOriginalPaymentId}>
              <SelectTrigger><SelectValue placeholder="Optional reference" /></SelectTrigger>
              <SelectContent>
                {eligiblePayments.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {new Date(p.recorded_at).toLocaleDateString()} · {p.method} · ${Number(p.applied_amount).toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Refund amount</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
            {overRefund && (
              <p className="text-sm text-destructive">Refund exceeds amount paid.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this refund being issued?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit}
            disabled={recordPayment.isPending || refundAmt <= 0 || overRefund || !reason.trim()}>
            {recordPayment.isPending ? 'Processing…' : 'Issue refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
